import { DefaultError, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInfiniteHandler } from 'hooks';
import { IInfinitePageParams, IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IReleases, ITopSalesChannels, IEarnings, IMusicProductDigitalEarnings } from './types';

export const useGetMusicProductDigitalReleased = (
  query?: IInfinitePageParams,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    query?.page,
    query?.itemCount
  );

  return useInfiniteQuery<IReleases[], DefaultError>({
    queryKey: ['music-product-digital-released', query],
    async queryFn({ pageParam }) {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/Released`,
        params: {
          ...JSON.parse(JSON.stringify(query)),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<IInfinitePageResponse<IReleases>>(
        response,
        {
          groupName: `useGetMusicProductDigitalReleased`,
          onNotFound: () => {},
        }
      );

      return infiniteHandler<IReleases>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled,
  });
};
export const useGetMusicProductDigitalUpcomingReleases = (
  query?: IInfinitePageParams,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    query?.page,
    query?.itemCount
  );

  return useInfiniteQuery<IReleases[], DefaultError>({
    queryKey: ['music-product-digital-upcoming-releases', query],
    async queryFn({ pageParam }) {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/UpcomingReleases`,
        params: {
          ...JSON.parse(JSON.stringify(query)),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<IInfinitePageResponse<IReleases>>(
        response,
        {
          groupName: `useGetMusicProductDigitalUpcomingReleases`,
          onNotFound: () => {},
        }
      );

      return infiniteHandler<IReleases>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled,
  });
};
// export const useGetMusicProductDigitalUpcomingReleases = (
//   query?: IInfinitePageParams,
//   enabled?: boolean
// ) => {
//   const { responseHandler } = useResponseHandler();
//   return useQuery<IReleases[], DefaultError>({
//     queryKey: ['music-product-digital-upcoming-releases', query],
//     async queryFn() {
//       const response = await apiClient({
//         method: 'GET',
//         url: `MusicProductDigital/UpcomingReleases`,
//         params: query,
//       });

//       const { items } = await responseHandler<IInfinitePageResponse<IReleases>>(
//         response,
//         {
//           groupName: `useGetMusicProductDigitalUpcomingReleases`,
//           onNotFound: () => {},
//         }
//       );

//       return items;
//     },
//     enabled,
//     staleTime: 5 * 60 * 1000,
//     refetchOnWindowFocus: false,
//   });
// };

export const useGetMusicProductDigitalTopSalesChannelsForProduct = (
  productId: string,
  startSettlementPeriodId: string,
  endSettlementPeriodId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<ITopSalesChannels[], DefaultError>({
    queryKey: [
      'music-product-digital-top-sales-channels-for-product',
      productId,
      startSettlementPeriodId,
      endSettlementPeriodId,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/SalesChannelForProduct`,
        params: {
          productId,
          startSettlementPeriodId,
          endSettlementPeriodId,
        },
      });

      const data = await responseHandler<ITopSalesChannels[]>(response, {
        groupName: `useGetMusicProductDigitalTopSalesChannelsForProduct`,
        onNotFound: () => {},
      });

      return data;
    },
    enabled: enabled && !!productId && !!startSettlementPeriodId && !!endSettlementPeriodId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetMusicProductDigitalEarningsBySalesChannel = (
  productId: string,
  salesChannelId: string,
  startSettlementPeriodId: string,
  endSettlementPeriodId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IEarnings[], DefaultError>({
    queryKey: [
      'music-product-digital-earnings-by-sales-channel',
      productId,
      salesChannelId,
      startSettlementPeriodId,
      endSettlementPeriodId,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/Earnings/SalesChannel`,
        params: {
          productId,
          salesChannelId,
          startSettlementPeriodId,
          endSettlementPeriodId,
        },
      });

      const data = await responseHandler<IEarnings[]>(response, {
        groupName: `useGetMusicProductDigitalEarningsBySalesChannel`,
        onNotFound: () => {},
      });

      return data;
    },
    enabled: enabled && !!productId && !!salesChannelId && !!startSettlementPeriodId && !!endSettlementPeriodId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetMusicProductDigitalEarnings = (
  productId: string,
  startSettlementPeriodId: string,
  endSettlementPeriodId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IMusicProductDigitalEarnings, DefaultError>({
    queryKey: [
      'music-product-digital-earnings',
      productId,
      startSettlementPeriodId,
      endSettlementPeriodId,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/Earnings`,
        params: {
          productId,
          startSettlementPeriodId,
          endSettlementPeriodId,
        },
      });

      const data = await responseHandler<IMusicProductDigitalEarnings>(response, {
        groupName: `useGetMusicProductDigitalEarnings`,
        onNotFound: () => {},
      });

      return data;
    },
    enabled: enabled && !!productId && !!startSettlementPeriodId && !!endSettlementPeriodId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
